import { Suspense } from 'react';
import { useDashboardBoot } from '../../hooks/useDashboardBoot';
import { FunnelSkeleton, CreatorsSkeleton, AssetsSkeleton } from '../../components/shared/ui/LazyFallbacks';

// Direct imports for instant loading - progressive rendering for optimal LCP
// Funnel renders first (above fold), then Creators, then Assets (below fold)
import FunnelSection from './FunnelSection';
import RecommendedCreators from './RecommendedCreators';
import LiveAssets from './LiveAssets';

/**
 * UpdatesSection - Optimized for Marlo.today-level performance
 * 
 * Strategy:
 * 1. Try single RPC call (dashboard_boot), fallback to 3 separate calls if RPC doesn't exist
 * 2. Progressive rendering: Funnel → Creators → Assets
 * 3. Skeleton screens for instant perceived load
 * 4. Boot data passed as props to avoid duplicate fetches
 */
const UpdatesSection = () => {
  // Tries dashboard_boot RPC first, then falls back to 3 parallel snapshot reads if RPC is not deployed.
  const { data: bootData, loading: bootLoading } = useDashboardBoot();
  
  return (
    <>
      {/* PHASE 1: Funnel - Renders immediately with skeleton fallback */}
      {/* This is above the fold, critical for LCP */}
      {bootLoading && !bootData ? (
        <FunnelSkeleton />
      ) : (
        <FunnelSection bootData={bootData?.funnel} />
      )}
      
      {/* PHASE 2: Recommended Creators - Renders after Funnel */}
      {/* Suspense ensures smooth progressive loading */}
      <Suspense fallback={<CreatorsSkeleton />}>
        {bootLoading && !bootData ? (
          <CreatorsSkeleton />
        ) : (
          <RecommendedCreators bootData={bootData?.creators} />
        )}
      </Suspense>
      
      {/* PHASE 3: Live Assets - Renders last (below fold) */}
      {/* Lazy loaded to reduce initial bundle size */}
      <Suspense fallback={<AssetsSkeleton />}>
        {bootLoading && !bootData ? (
          <AssetsSkeleton />
        ) : (
          <LiveAssets bootData={bootData?.assets} />
        )}
      </Suspense>
    </>
  );
};

UpdatesSection.displayName = 'UpdatesSection';

export default UpdatesSection;
